import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, FileText } from "lucide-react";
import { Reveal, SectionHead } from "@/components/Reveal";

const PAGES = 32;
const pageSrc = (n) =>
  `/assets/leseprobe/seite-${String(n).padStart(2, "0")}.jpg`;

const Leseprobe = () => {
  const [page, setPage] = useState(1);
  const prev = () => setPage((p) => Math.max(1, p - 1));
  const next = () => setPage((p) => Math.min(PAGES, p + 1));

  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") prev();
    if (e.key === "ArrowRight") next();
  };

  return (
    <section
      id="leseprobe"
      data-testid="leseprobe-section"
      className="relative py-24 sm:py-32 bg-navy-900 border-y border-white/5 overflow-hidden"
    >
      <div
        aria-hidden="true"
        className="absolute -bottom-40 -left-32 h-[480px] w-[480px] rounded-full bg-[radial-gradient(circle,rgba(0,102,255,0.14)_0%,rgba(13,20,36,0)_70%)]"
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <SectionHead
            overline="Leseprobe"
            title="Lesen Sie rein."
            sub="Blättern Sie direkt hier durch die Leseprobe: 32 Seiten erster Einblick in Aufbau, Sprache und Themen von BlaulichtPraxis – Datenschutz."
          />
          <Reveal delay={0.15}>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <div
                className="inline-flex items-center rounded-full border border-white/10 bg-navy-800/70 p-1"
                role="group"
                aria-label="Leseprobe blättern"
              >
                <button
                  type="button"
                  data-testid="leseprobe-prev"
                  onClick={prev}
                  disabled={page === 1}
                  aria-label="Vorherige Seite"
                  className="flex h-10 w-10 items-center justify-center rounded-full text-gray-300 transition-colors duration-200 hover:bg-blau hover:text-white disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-300"
                >
                  <ChevronLeft size={18} aria-hidden="true" />
                </button>
                <span
                  data-testid="leseprobe-counter"
                  className="min-w-[110px] text-center font-mono text-xs uppercase tracking-[0.2em] text-gray-400"
                >
                  Seite {page} / {PAGES}
                </span>
                <button
                  type="button"
                  data-testid="leseprobe-next"
                  onClick={next}
                  disabled={page === PAGES}
                  aria-label="Nächste Seite"
                  className="flex h-10 w-10 items-center justify-center rounded-full text-gray-300 transition-colors duration-200 hover:bg-blau hover:text-white disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-gray-300"
                >
                  <ChevronRight size={18} aria-hidden="true" />
                </button>
              </div>
              <a
                href="/assets/leseprobe.pdf"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="leseprobe-open-button"
                className="inline-flex items-center gap-2 text-sm font-semibold text-blau-glow transition-colors duration-200 hover:text-white"
              >
                <FileText size={15} aria-hidden="true" />
                Als PDF öffnen
              </a>
            </div>
            <div
              aria-hidden="true"
              className="mt-8 h-px w-full max-w-md bg-white/10"
            >
              <div
                className="h-px bg-blau transition-[width] duration-300"
                style={{ width: `${(page / PAGES) * 100}%` }}
              />
            </div>
          </Reveal>
        </div>

        <Reveal className="flex justify-center">
          <div
            className="relative w-full max-w-[340px] sm:max-w-[380px]"
            data-testid="leseprobe-stage"
            tabIndex={0}
            role="region"
            aria-label={`Leseprobe, Seite ${page} von ${PAGES}. Pfeiltasten zum Blättern.`}
            onKeyDown={onKeyDown}
          >
            {page < PAGES && (
              <img
                src={pageSrc(page + 1)}
                alt=""
                aria-hidden="true"
                className="absolute inset-0 w-full translate-x-3 translate-y-3 rotate-2 rounded-md border border-white/10 opacity-40"
                loading="lazy"
              />
            )}
            <div
              aria-hidden="true"
              className="absolute -inset-8 rounded-full bg-[radial-gradient(circle,rgba(0,102,255,0.18)_0%,rgba(13,20,36,0)_70%)]"
            />
            <AnimatePresence mode="wait">
              <motion.img
                key={page}
                src={pageSrc(page)}
                alt={`Leseprobe BlaulichtPraxis – Datenschutz, Seite ${page}`}
                data-testid="leseprobe-page-image"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                className="book-shadow relative w-full rounded-md border border-white/10 bg-navy-800"
                loading={page === 1 ? "eager" : "lazy"}
              />
            </AnimatePresence>
            {page < PAGES && (
              <img src={pageSrc(page + 1)} alt="" aria-hidden="true" className="hidden" />
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Leseprobe;
